import * as React from "react";
import { format, parseISO } from "date-fns";
import { Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@nfp-tracker/components";
import { cn, formatTemperature, getCurrentCycle, getTodayKey, type NFPMethod } from "@nfp-tracker/utils";
import { ChartFullscreenDialog, FullscreenButton } from "./ChartFullscreenDialog";
import { MucusMark, OpkDiamond, SingleCycleChart } from "./CycleChartCanvas";
import { HEADER_HEIGHT, LANE_HEIGHT, PHASE_STRIP_HEIGHT } from "./cycleChartStyles";
import {
  buildCycleChartModel,
  buildCycleOptions,
  getCycleHistoryStats,
  showThermalMarkersByDefault,
  type ChartCycle,
  type ChartObservation,
  type CycleChartModel,
  type OptionalFields,
} from "./cycleChartData";
import { fitColumnWidth, useElementSize } from "./useChartLayout";

export interface CycleChartCardProps {
  /** Newest first, as the observations cache returns them. */
  cycles: ChartCycle[];
  observations: ChartObservation[];
  methods: string[];
  isPostpartum: boolean;
  optionalFields: OptionalFields;
  /** Today's date key in the owner's timezone; decides current / past / upcoming cycles. */
  today?: string;
  /**
   * False when the cycles come from the ~13-month cache, where the oldest cycle's start may
   * simply predate what's loaded (worded like Cycle History's "start not in loaded history").
   */
  hasFullHistory?: boolean;
  className?: string;
}

const INLINE_TEMPERATURE_HEIGHT = 200;
const MIN_FULLSCREEN_TEMPERATURE_HEIGHT = 140;
/** Room left for the horizontal scrollbar under the chart in full screen. */
const SCROLLBAR_ALLOWANCE = 14;

const LegendItem = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1.5">{children}</span>
);

/** What the cover line and temperature shift are, in plain words. Opens on tap as well as click. */
export const ThermalInfo = () => (
  <Popover>
    <PopoverTrigger
      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="What are the cover line and temperature shift?"
    >
      <Info className="h-3.5 w-3.5" />
    </PopoverTrigger>
    <PopoverContent side="top" className="w-72 space-y-2 p-3 text-xs">
      <p>
        <span className="font-semibold">Temperature shift</span>: the first of three waking
        temperatures in a row that sit at least 0.4°F above the six before them. It usually means
        ovulation has passed. Those three temps are circled.
      </p>
      <p>
        <span className="font-semibold">Cover line</span>: the average of those six earlier
        temperatures plus 0.4°F, drawn so you can see the shift clear it. Until a shift is found, a
        provisional line from the cycle's first six temperatures is shown instead.
      </p>
      <p className="text-muted-foreground">
        Shown for reference only. Follow your method's own rules and your instructor's guidance.
      </p>
    </PopoverContent>
  </Popover>
);

const CycleLegend = ({ model, showThermal }: { model: CycleChartModel; showThermal: boolean }) => {
  const lanes = new Set(model.lanes);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
      {model.hasTemperatures ? (
        <LegendItem>
          <span className="block h-0.5 w-4 rounded bg-emerald-600 dark:bg-emerald-400" />
          Temperature
        </LegendItem>
      ) : null}
      {model.spans.some((span) => span.key === "fertile") ? (
        <LegendItem>
          <span className="block h-3 w-4 rounded-sm bg-green-500/20 dark:bg-green-400/20" />
          Fertile window
        </LegendItem>
      ) : null}
      {model.peakCd ? (
        <LegendItem>
          <span className="block h-3 w-4 rounded-sm bg-violet-600/25 dark:bg-violet-400/25" />
          Peak Day
        </LegendItem>
      ) : null}
      {showThermal && model.coverLine ? (
        <LegendItem>
          <span className="block w-4 border-t-2 border-dashed border-sky-600 dark:border-sky-400" />
          Cover line {formatTemperature(model.coverLine.value, "°F")}
          {model.coverLine.basis === "provisional" ? " (provisional)" : ""}
        </LegendItem>
      ) : null}
      {showThermal && model.thermalShiftCd ? (
        <LegendItem>
          <span className="block h-3 w-3 rounded-full border-[1.5px] border-orange-500 dark:border-orange-400" />
          Temp shift {model.partial ? "" : `CD ${model.thermalShiftCd}`}
        </LegendItem>
      ) : null}
      {lanes.has("opk") ? (
        <LegendItem>
          <OpkDiamond positive /> OPK + <OpkDiamond positive={false} /> OPK −
        </LegendItem>
      ) : null}
      {lanes.has("mucus") ? (
        <LegendItem>
          <MucusMark kind="peak" /> peak-type mucus
          <MucusMark kind="mucus" /> other
        </LegendItem>
      ) : null}
    </div>
  );
};

export const CycleChartCard = ({
  cycles,
  observations,
  methods,
  isPostpartum,
  optionalFields,
  today: todayProp,
  hasFullHistory = false,
  className,
}: CycleChartCardProps) => {
  const baseId = React.useId();
  const today = todayProp ?? getTodayKey();
  // null = follow the default (the cycle containing today), so it updates as data changes.
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [showThermal, setShowThermal] = React.useState(() => showThermalMarkersByDefault(methods));
  const [fullscreen, setFullscreen] = React.useState(false);
  const [inlineRef, inlineSize] = useElementSize<HTMLDivElement>();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const fullScrollRef = React.useRef<HTMLDivElement | null>(null);

  // Fall back to the newest cycle if the selected one disappears (e.g. a new cycle started).
  // Pre-logged future data can make the newest cycle an upcoming one, so the default is the
  // cycle containing today, not cycles[0].
  const selectedCycle =
    cycles.find((cycle) => cycle.id === selectedId) ??
    getCurrentCycle(cycles, today) ??
    cycles.find((cycle) => cycle.start_date <= today) ??
    cycles[0] ??
    null;

  const cycleOptions = React.useMemo(
    () => buildCycleOptions(cycles, observations, today, { hasFullHistory }),
    [cycles, observations, today, hasFullHistory]
  );

  const methodsKey = methods.join(",");
  const model = React.useMemo(() => {
    if (!selectedCycle) return null;
    return buildCycleChartModel(
      selectedCycle,
      observations.filter((obs) => obs.cycle_id === selectedCycle.id),
      {
        methods: methods as NFPMethod[],
        isPostpartum,
        optionalFields,
        today,
        ...getCycleHistoryStats(cycles, today),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycle, cycles, observations, methodsKey, isPostpartum, optionalFields, today]);

  const inlineColumnWidth = fitColumnWidth(inlineSize.width, model?.length ?? 0, false);

  // Running cycle: start scrolled to the latest days, which are the ones being watched.
  React.useEffect(() => {
    if (!model?.todayCd) return;
    for (const node of [scrollRef.current, fullScrollRef.current]) {
      if (!node) continue;
      // Centre today (later days may already be pre-logged).
      const columnWidth = node.scrollWidth / Math.max(1, model.length);
      node.scrollLeft = Math.max(0, (model.todayCd - 0.5) * columnWidth - node.clientWidth / 2);
    }
  }, [model?.cycleId, model?.todayCd, model?.length, inlineColumnWidth, fullscreen]);

  if (!selectedCycle || !model) return null;

  const fertile = model.spans.find((span) => span.key === "fertile");
  const peakDate = model.peakCd ? model.days[model.peakCd - 1]?.date : null;
  const summary = [
    model.peakCd
      ? model.partial && peakDate
        ? `Peak Day ${format(parseISO(peakDate), "MMM d")}`
        : `Peak Day CD ${model.peakCd}`
      : "No Peak Day identified yet",
    // An upcoming cycle's window hasn't opened yet — "CD 6–open" would read as a live window.
    fertile && !model.partial && !model.isUpcoming
      ? `Fertile window CD ${fertile.startCd}–${fertile.open ? "open" : fertile.endCd}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Rendered twice (card + full-screen dialog), so each copy gets its own ids.
  const renderControls = (idPrefix: string) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="min-w-0 flex-1 basis-56 sm:max-w-xs">
        <Label htmlFor={`${idPrefix}-cycle`} className="sr-only">
          Cycle
        </Label>
        <Select value={selectedCycle.id} onValueChange={setSelectedId}>
          <SelectTrigger id={`${idPrefix}-cycle`} aria-label="Choose a cycle" className="h-9">
            {/* Trigger shows the label only; the detail line is for the open list. */}
            <SelectValue>{cycleOptions.find((option) => option.id === selectedCycle.id)?.label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {cycleOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                <span>{option.label}</span>
                {option.detail ? (
                  <span className="ml-2 text-xs text-muted-foreground">{option.detail}</span>
                ) : null}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {model.hasTemperatures ? (
        <div className="flex items-center gap-1.5">
          <Switch id={`${idPrefix}-thermal`} checked={showThermal} onCheckedChange={setShowThermal} />
          <Label htmlFor={`${idPrefix}-thermal`} className="text-xs font-normal">
            Cover line &amp; temp shift
          </Label>
          <ThermalInfo />
        </div>
      ) : null}
    </div>
  );

  const legend = <CycleLegend model={model} showThermal={showThermal} />;
  const description = [
    summary,
    model.partial
      ? hasFullHistory
        ? "Cycle start not recorded, so days are shown by date."
        : "This cycle's start isn't in the loaded history, so days are shown by date."
      : null,
    model.isUpcoming ? "Upcoming: this cycle hasn't started yet; showing entries logged ahead." : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card className={cn("min-w-0", className)}>
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <CardTitle>Cycle Chart</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <FullscreenButton onClick={() => setFullscreen(true)} label="Open cycle chart full screen" />
        </div>
        {renderControls(`${baseId}-card`)}
      </CardHeader>
      <CardContent className="min-w-0 space-y-3">
        {legend}
        <div ref={inlineRef} className="min-w-0">
          {/* Stays mounted under the full-screen dialog so the page behind it doesn't reflow. */}
          <SingleCycleChart
            model={model}
            columnWidth={inlineColumnWidth}
            temperatureHeight={INLINE_TEMPERATURE_HEIGHT}
            showThermalMarkers={showThermal}
            scrollRef={scrollRef}
          />
        </div>
      </CardContent>

      <ChartFullscreenDialog
        open={fullscreen}
        onOpenChange={setFullscreen}
        title="Cycle Chart"
        description={description}
        controls={renderControls(`${baseId}-full`)}
        toolbar={legend}
      >
        {(size) => {
          const fixedRows =
            HEADER_HEIGHT +
            (model.spans.length ? PHASE_STRIP_HEIGHT : 0) +
            model.lanes.length * LANE_HEIGHT +
            SCROLLBAR_ALLOWANCE;
          return (
            <SingleCycleChart
              model={model}
              columnWidth={fitColumnWidth(size.width, model.length, true)}
              temperatureHeight={Math.max(MIN_FULLSCREEN_TEMPERATURE_HEIGHT, Math.floor(size.height - fixedRows))}
              showThermalMarkers={showThermal}
              scrollRef={fullScrollRef}
            />
          );
        }}
      </ChartFullscreenDialog>
    </Card>
  );
};
